// constants
import {
    SORT_BY_VALUES,
    BATCH_TYPES
} from "frontend/components/fort/mod/photos/constants.js";

// CONFIG
const DEBUG_BATCH_LIMIT_ON = false;
const DEBUG_BATCH_LIMIT = 20;

// helpers
import batchApi from "js/utils/batchApi.js";

// scripts
import { enhanceBatches } from "./enhanceBatches.js";
import { fetchBatches } from "./clientApi.js";

/**
 * batchFetcher
 * Originally was set up in case we want to set up various recipe fetches for optimization
 * When it is all said and done, we should always fetchAll to make sure we are in sync with backend changes
 * So we currently only have one recipe: getAllPages()
 * @param {Object} props
 * @returns methods for fetching
 */
export const batchFetcher = props => {
    const {
        batchesPerPage,
        moderationType,
        showOnlySuspendedCreators,
        textSearch,
        batchType,
        sortBy
    } = props;

    // Shared GraphQL Variables
    let gqlBaseVariables = {
        limit: batchesPerPage,
        moderationType,
        showOnlySuspendedCreators,
        textSearch,
        ...getFilterFlags(batchType),
        // Default sorting is oldest to newest by date submitted unless sortNewestToOldest is True."
        sortNewestToOldest: sortBy === SORT_BY_VALUES[1]
    };

    // Normalized Response Object after fetching
    const getNormalizedResponse = async (
        newCurrentPage,
        newBatches,
        newTotalCount
    ) => {
        // Enhance Batches
        const enhancedBatches = await enhanceBatches({
            ...props,
            batches: newBatches
        });

        return {
            enhancedBatches,
            newTotalCount,
            newCurrentPage
        };
    };

    // Fetching Recipes
    const getAllPages = async page => {
        const data = await getBatches({
            ...gqlBaseVariables,
            offset: 0,
            limit: batchesPerPage * page
        });

        const { batches: newBatches, totalCount: newTotalCount } = data;

        const newCurrentPage = getCurrentPage(
            page,
            newBatches.length,
            batchesPerPage
        );

        const normalizedResp = await getNormalizedResponse(
            newCurrentPage,
            newBatches,
            newTotalCount
        );

        return normalizedResp;
    };

    return {
        getAllPages
    };
};

/**
 * limitBatches A way to simulate/control response from featching batches (testing 20 batches for example)
 * @param {Object} batchesObj {items, totalCount} response from BE
 * @returns
 */
export const limitBatches = batchesObj => {
    const batches = batchesObj.batches.slice(0, DEBUG_BATCH_LIMIT);
    const totalCount =
        batchesObj.totalCount < DEBUG_BATCH_LIMIT
            ? batchesObj.totalCount
            : DEBUG_BATCH_LIMIT;

    return {
        batches,
        totalCount
    };
};

/**
 * getBatches
 * @param {Object} variables :: gqlVariables
 * @returns {Object} { batches, totalCount }
 */
export const getBatches = async variables => {
    const { limit } = variables;
    const sizePerBatch = 100;
    let batches = [];
    let totalCount = 0;

    const batchSizes = batchApi.createBatchSizesFromNumber(limit, sizePerBatch);
    const responses = await batchApi.calls(
        batchSizes,
        async (batchSize, idx) => {
            return await fetchBatches(variables, batchSize, sizePerBatch * idx);
        }
    );

    responses.forEach(p => {
        const { items, totalCount: respTotalCount } = p;
        // this should be the same for every query
        totalCount = respTotalCount;
        batches = [...batches, ...items];
    });

    const fetchBatchData = {
        batches,
        totalCount
    };

    return DEBUG_BATCH_LIMIT_ON ? limitBatches(fetchBatchData) : fetchBatchData;
};

/**
 * getCurrentPage
 * @param {Array} batches
 * @param {Number} batchesPerPage
 * @returns {Number} newCurrentPage
 */
const getCurrentPage = (page, fetchedBatchCount, batchesPerPage) => {
    let newCurrentPage = page;

    // make sure the newCurrentPage supports already fetched data
    if (fetchedBatchCount > 0) {
        newCurrentPage = Math.ceil(fetchedBatchCount / batchesPerPage);
    }

    return newCurrentPage;
};

/**
 * getFilterFlags - only add flags if they are true, backend ignores missing variables.
 * @param {Object} batchType state of batch type filter flags
 * @returns
 */
export const getFilterFlags = batchType => {
    let filterFlags = {};

    BATCH_TYPES.forEach(({ key, databaseVariable }) => {
        // only add if true
        if (batchType[key]) {
            filterFlags[databaseVariable] = true;
        }
    });

    return filterFlags;
};
